import React, { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { payment, formatCurrency } from '@futurefamily/ef-utils';
import {
  Button,
  PaymentOptionContainer,
  PaymentOptionItem as DPaymentOptionItem,
  PaymentOptionItemExtras
} from '@futurefamily/ef-components';
import * as config from 'config';

const PaymentOptionItem = styled(DPaymentOptionItem)`
  --background-color: var(--white);
  --separator-color: var(--white);

  padding-top: 15px;
  padding-bottom: 15px;
  color: var(--white);

  @media (min-width: 768px) {
    grid-template-rows: 68px 95px 1fr;
  }

  border: 1px solid var(--border-color);
  border-width: 1px;

  ${PaymentOptionItemExtras.Name} {
    font-size: 22px;
  }

  ${PaymentOptionItemExtras.OptionNumber} {
    font-size: 16px;
    font-weight: 400;
  }

  ${PaymentOptionItemExtras.Cost} {
    font-size: 24px;
  }

  ${PaymentOptionItemExtras.Term} {
    font-size: 12px;
  }

  ${PaymentOptionItemExtras.Disclosure} {
    margin-top: 5px;
    font-size: 12px;
  }

  ${Button} {
    background-color: var(--white);
    font-weight: 700;
    color: var(--teal);
  }
`;

function PendingCost ({ amount, children, required, alwaysShow }) {
  if (alwaysShow) { return children; }

  if (amount === '') {
    return <strong>Enter your amount</strong>;
  }

  if (amount < required) {
    return <strong>Minimum loan of ${formatCurrency(required)} needed</strong>
  }

  return children;
}

const useOptions = () => {
  const { values } = useFormikContext();
  const { term, cost, downpayment } = values;
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const prequalUrl = `${config.APP_FLOW_URL}?${config.UTM_PARAMETERS}&vendorId=${config.VENDOR_ID}&vendorLocationId=${query.get('vendor')}`;

  return useMemo(() => {
    const fullFinance = payment.getMonthlyPayment(cost, term, config.APR).toFixed(0);
    const partialLoanAmount = cost - downpayment;
    const partialFinance = payment.getMonthlyPayment(partialLoanAmount, term, config.APR).toFixed(0);
    const percentFinanced = Math.round((partialLoanAmount / cost) * 100);

    const showPartialFinance = cost >= config.LOAN_MINIMUM + config.DOWNPAYMENT_MINIMUM;
    const showFullFinance = cost >= config.LOAN_MINIMUM;

    return [
      {
        name: 'Out of Pocket',
        paymentInfo: 'Upfront payment',
        cost: (
          <PendingCost amount={cost} required={0}>
            <strong>
              ${formatCurrency(cost)}
            </strong>
          </PendingCost>
        ),
        apr: cost ? 'At time of service' : '',
        style: {
          '--background-color': 'var(--teal)',
          '--border-color': "var(--teal)",
        },
        Button: () => <Button id="schedule-appointment" className="px-2" as="a" href={config.RETURN_URL}>SCHEDULE APPOINTMENT</Button>
      },
      {
        disabled: !showFullFinance,
        name: 'Buy Now Pay Later',
        paymentInfo: 'Monthly payments',
        cost: (
          <PendingCost amount={cost} required={config.LOAN_MINIMUM}>
            <strong>${formatCurrency(fullFinance)}</strong>/month*
          </PendingCost>
        ),
        apr: showFullFinance ? `For ${term} months at ${String(config.APR)}%` : '',
        showDisclosure: true,
        style: {
          '--background-color': 'var(--brown)',
          '--border-color': "var(--brown)",
        },
        Button: () => <Button id="full-finance" as="a" href={`${prequalUrl}&amount=${cost}&term=${term}`}>PREQUALIFY NOW</Button>
      },
      {
        disabled: !showPartialFinance,
        name: 'Partial Finance',
        paymentInfo: showPartialFinance ? `${100 - percentFinanced}% cash + ${percentFinanced}% financing` : 'Some payment and financing',
        cost: (
          <PendingCost
            amount={!cost ? '' : downpayment < 0 ? 0 : cost}
            required={config.LOAN_MINIMUM + config.DOWNPAYMENT_MINIMUM}
          >
            <div>
              <strong>${formatCurrency(downpayment)}</strong> cash
            </div>
            <div>
              +<strong>${formatCurrency(partialFinance)}</strong>/month*
            </div>
          </PendingCost>
        ),
        apr: showPartialFinance ? `For ${term} months at ${String(config.APR)}%` : '',
        showDisclosure: true,
        style: {
          '--background-color': 'var(--black)',
          '--border-color': 'var(--black)',
        },
        Button: () => <Button id="partial-finance" as="a" href={`${prequalUrl}&amount=${partialLoanAmount}&term=${term}`}>PREQUALIFY NOW</Button>
      }
    ];
  }, [downpayment, cost, term, prequalUrl]);
};

function PaymentOptions () {
  const options = useOptions();

  return (
    <PaymentOptionContainer className="mb-4">
      {options.map((option, i) => (
        <PaymentOptionItem
          key={i}
          number={i + 1}
          {...option}
        />
      ))}
    </PaymentOptionContainer>
  );
}

export default PaymentOptions;
